<script>
    import { onMount, onDestroy } from 'svelte';
    import { Link } from "svelte-routing";
    import Markdown from 'svelte-markdown';
    import allContent from "../data/content.json";

    export let id;

    let contentItem = null;
    let markdownContent = "";
    let errorMessage = "";
    let scriptElement = null; // To keep track of dynamically added script

    onMount(async () => {
        contentItem = allContent.find(item => item.id === id);

        if (contentItem) {
            if (contentItem.mdFile) {
                try {
                    const response = await fetch(`/Contents/${contentItem.mdFile}`);
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    markdownContent = await response.text();
                } catch (error) {
                    console.error("Error loading markdown file:", error);
                    errorMessage = "콘텐츠를 불러오는 데 실패했습니다.";
                }
            } else if (contentItem.jsFile) {
                try {
                    scriptElement = document.createElement('script');
                    scriptElement.src = `/Contents/${contentItem.jsFile}`;
                    scriptElement.async = true;
                    document.body.appendChild(scriptElement);

                    // Optional: If the JS file exports a function to render, call it here
                    // For now, just loading the script is enough.
                    // You might need a way for the JS file to signal it's ready or to expose a global function.

                } catch (error) {
                    console.error("Error loading JavaScript file:", error);
                    errorMessage = "인터프리터 스크립트를 불러오는 데 실패했습니다.";
                }
            } else {
                errorMessage = "콘텐츠 형식을 알 수 없습니다.";
            }
        } else {
            errorMessage = "콘텐츠를 찾을 수 없습니다.";
        }
    });

    onDestroy(() => {
        if (scriptElement && scriptElement.parentNode) {
            scriptElement.parentNode.removeChild(scriptElement);
        }
    });
</script>

<main class="page-shell">
    {#if contentItem}
        <article class="detail-shell glass-panel">
            <header class="detail-header">
                <div class="detail-meta">
                    <span class="section-kicker">Content Detail</span>
                    <p class="tags">{contentItem.tags.join(' ')}</p>
                </div>
                <div class="header-main">
                    <div>
                        <h1>{contentItem.title}</h1>
                        <p class="description">{contentItem.description}</p>
                    </div>
                    <div class="header-side">
                        <p class="date">{contentItem.date}</p>
                        <Link to="/" class="back-link">홈으로</Link>
                    </div>
                </div>
            </header>

            {#if contentItem.mdFile}
                <div class="markdown-body">
                    <Markdown source={markdownContent} />
                </div>
            {:else if contentItem.jsFile}
                <div class="js-content-container">
                    <div id="python-interpreter-target"></div>
                </div>
            {/if}
        </article>

    {:else if errorMessage}
        <p class="error-message">{errorMessage}</p>
    {:else}
        <p>로딩 중...</p>
    {/if}
</main>

<style>
    .detail-shell {
        border-radius: var(--radius-xl);
        padding: 42px;
        overflow: hidden;
        position: relative;
        background: rgba(255, 255, 255, 0.82);
        backdrop-filter: blur(24px) saturate(1.5);
        -webkit-backdrop-filter: blur(24px) saturate(1.5);
        border: 1px solid rgba(255, 255, 255, 0.8);
        box-shadow: var(--shadow-lg);
    }

    .detail-shell::after {
        content: "";
        position: absolute;
        top: -80px;
        right: -40px;
        width: 280px;
        height: 280px;
        background: radial-gradient(circle, rgba(15, 98, 254, 0.08), transparent 70%);
        pointer-events: none;
    }

    .detail-header {
        display: grid;
        gap: 22px;
        padding-bottom: 28px;
        border-bottom: 1px solid var(--outline);
        position: relative;
        z-index: 1;
    }

    .detail-meta {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: center;
    }

    .detail-meta .section-kicker {
        color: var(--primary);
    }

    .header-main {
        display: flex;
        justify-content: space-between;
        gap: 24px;
        align-items: start;
    }

    h1 {
        font-size: clamp(3rem, 5vw, 5rem);
        line-height: 0.95;
        margin-bottom: 12px;
        max-width: 12ch;
        letter-spacing: -0.035em;
    }

    .description {
        max-width: 62ch;
        font-size: 1.08rem;
    }

    .tags,
    .date {
        color: var(--muted);
        font-size: 0.9rem;
    }

    .header-side {
        display: grid;
        gap: 14px;
        justify-items: end;
        min-width: 120px;
    }

    :global(a.back-link) {
        text-decoration: none;
        padding: 12px 20px;
        border-radius: 12px;
        border: none;
        background: var(--primary);
        color: var(--on-primary);
        font-weight: 600;
        font-size: 0.88rem;
        box-shadow: 0 2px 8px rgba(15, 98, 254, 0.2);
        transition: background 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
    }

    :global(a.back-link:hover) {
        background: var(--accent-strong);
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
    }

    .markdown-body {
        padding-top: 28px;
        line-height: 1.85;
        color: var(--text);
        position: relative;
        z-index: 1;
    }

    .markdown-body :global(h1),
    .markdown-body :global(h2),
    .markdown-body :global(h3) {
        margin: 2.5rem 0 1rem;
        letter-spacing: -0.02em;
    }

    .markdown-body :global(p),
    .markdown-body :global(li) {
        color: var(--text);
    }

    .markdown-body :global(code) {
        background: rgba(15, 98, 254, 0.08);
        color: var(--primary);
        padding: 0.15rem 0.4rem;
        border-radius: 6px;
        font-size: 0.92em;
    }

    .markdown-body :global(pre) {
        overflow: auto;
        padding: 18px;
        border-radius: 16px;
        background: #141518;
        color: #e8ecf4;
        border: 1px solid rgba(255, 255, 255, 0.06);
    }

    .markdown-body :global(pre code) {
        background: transparent;
        color: inherit;
        padding: 0;
    }

    .error-message {
        color: var(--accent-strong);
        text-align: center;
        margin-top: 50px;
    }

    .js-content-container {
        margin-top: 28px;
        border: 1px solid var(--outline);
        border-radius: var(--radius-xl);
        padding: 24px;
        min-height: 200px;
        background: rgba(255, 255, 255, 0.76);
    }

    @media (max-width: 900px) {
        .detail-shell {
            padding: 24px;
        }

        .detail-meta,
        .header-main {
            flex-direction: column;
            align-items: start;
        }

        .header-side {
            justify-items: start;
        }
    }
</style>
